import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Card, Snackbar, Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchEvents,
  fetchCourses,
  fetchSpreadsheetData,
  selectEvents,
  selectEventsStatus,
  selectCourses,
  selectCoursesStatus,
} from 'src/v3/content-pipeline/performance.slice'
import { DownloadIcon } from 'src/v3/core/icons'
import { pluckError } from 'src/utilities/http-client'
import { selectRoles } from 'src/v3/settings/settings.slice'
import { SentimentAnalysisSection } from './components/sentiment-analysis-section'
import {
  PerformanceFilters,
  TimeRange,
  LicenseCategory,
} from './components/performance-filters'
import { EventList } from './components/event-list'
import { SectionTabs, useSectionObserver } from './components/section-tabs'
import { LearningAnalyticsSection } from './components/learning-analytics-section'
import { LikertAnalysisSection } from './components/likert-analysis-section'
import { NurseGrowthBanner } from './components/nurse-growth-banner'

type ContentDomain = 'live' | 'enduring'

type PerformanceDashboardProps = {
  domain: ContentDomain
}

export default ({ domain }: PerformanceDashboardProps): ReactElement => {
  let dispatch = useAppDispatch()
  let detailRef = useRef<HTMLDivElement>(null)

  let [timeRange, setTimeRange] = useState<TimeRange>('1')
  let [licenseCategory, setLicenseCategory] =
    useState<LicenseCategory>('Paramedic')
  let [activeEvent, setActiveEvent] = useState<string>()
  let [startDate, setStartDate] = useState<Dayjs | null>(null)
  let [endDate, setEndDate] = useState<Dayjs | null>(dayjs())
  let [loading, setLoading] = useState(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let roles = useAppSelector(selectRoles)
  let events = useAppSelector(selectEvents)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  let { sections, activeSection, registerRef, scrollToSection } =
    useSectionObserver()

  let items = domain === 'live' ? events : courses
  let itemsStatus = domain === 'live' ? eventsStatus : coursesStatus

  let dateRange = useMemo(() => {
    let rangeEndDate = dayjs()
    let rangeStartDate = dayjs().subtract(parseInt(timeRange, 10), 'months')
    return {
      startDate: rangeStartDate.format('YYYY-MM-DD'),
      endDate: rangeEndDate.format('YYYY-MM-DD'),
    }
  }, [timeRange])

  let selectedItemName = useMemo(() => {
    if (!activeEvent) return null
    if (domain === 'live') return events.find((e) => e.id === activeEvent)?.name
    return courses.find((c) => c.id === activeEvent)?.name
  }, [activeEvent, domain, events, courses])

  useEffect(() => {
    if (domain === 'live' && eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    } else if (domain === 'enduring' && coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [domain, eventsStatus, coursesStatus, dispatch])

  useEffect(() => {
    if (detailRef.current && activeEvent && window.innerWidth < 900) {
      let prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      detailRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      })
    }
  }, [activeEvent])

  return (
    <Box>
      {roles.includes('admin') && (
        <Card sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Generate Course & Event Spreadsheet Data
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: { sx: { bgcolor: 'background.paperDark' } },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: { sx: { bgcolor: 'background.paperDark' } },
                }}
              />
            </LocalizationProvider>
            <Button
              startIcon={<DownloadIcon />}
              loading={loading}
              variant="contained"
              size="large"
              disabled={!startDate || !endDate}
              onClick={() => {
                setLoading(true)
                dispatch(
                  fetchSpreadsheetData({
                    startDate: startDate?.format('MM/DD/YYYY'),
                    endDate: endDate?.format('MM/DD/YYYY'),
                  }),
                )
                  .catch((err) =>
                    setErrorMessage(
                      pluckError(err, 'Error fetching spreadsheet data'),
                    ),
                  )
                  .finally(() => setLoading(false))
              }}
            >
              Download CSV
            </Button>
          </Box>
        </Card>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
            {domain === 'live' ? 'Live Classes (F5)' : 'Enduring Courses (F3)'}{' '}
            - Performance Dashboard
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Sentiment, ratings, and learning analytics.
            {activeEvent
              ? ` Switch ${domain === 'live' ? 'event' : 'course'} in the sidebar to compare.`
              : ` Select ${domain === 'live' ? 'an event' : 'a course'} to begin.`}
          </Typography>
        </Box>
      </Box>
      <NurseGrowthBanner />
      <PerformanceFilters
        timeRange={timeRange}
        licenseCategory={licenseCategory}
        onTimeRangeChange={setTimeRange}
        onLicenseCategoryChange={setLicenseCategory}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { sm: '1fr', md: '350px 1fr' },
          gap: 3,
          alignItems: 'start',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            position: { md: 'sticky' },
            top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 32px)' },
            overflowY: { md: 'auto' },
            minWidth: 0,
          }}
        >
          <EventList
            events={items}
            eventsStatus={itemsStatus}
            activeEventId={activeEvent}
            onEventSelect={setActiveEvent}
            listTitle={domain === 'live' ? 'Events' : 'Courses'}
          />
        </Box>

        <Box
          ref={detailRef}
          role="main"
          aria-label="Performance details"
          sx={{ minWidth: 0 }}
        >
          {!activeEvent ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'background.paperDark',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                Select {domain === 'live' ? 'an Event' : 'a Course'}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Choose {domain === 'live' ? 'an event' : 'a course'} from the
                list to view performance metrics and analytics.
              </Typography>
            </Box>
          ) : (
            <>
              <Card variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 700 }}
                >
                  Performance: {selectedItemName}
                </Typography>
              </Card>

              <SectionTabs
                sections={sections}
                activeSection={activeSection}
                onSectionClick={scrollToSection}
              />

              <Box
                ref={registerRef('learning-analytics')}
                data-section-id="learning-analytics"
                sx={{ pt: 3, mb: 4 }}
              >
                <LearningAnalyticsSection
                  domain={domain}
                  itemID={activeEvent}
                  timeRangeMonths={parseInt(timeRange, 10)}
                  licenseFilter={licenseCategory}
                />
              </Box>

              <Box
                ref={registerRef('likert-averages')}
                data-section-id="likert-averages"
                sx={{ pt: 3, mb: 4 }}
              >
                <LikertAnalysisSection
                  domain={domain}
                  itemID={activeEvent}
                  licenseGroup={licenseCategory}
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                />
              </Box>

              <Box
                ref={registerRef('sentiment-analysis')}
                data-section-id="sentiment-analysis"
                sx={{ pt: 3, mb: 4 }}
              >
                <SentimentAnalysisSection
                  domain={domain}
                  itemID={activeEvent}
                  licenseGroup={licenseCategory}
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
